import React from 'react'
import styled from 'styled-components/native'

import { useLogoutRoutine } from 'features/auth/helpers/useLogoutRoutine'
import { useAccountSuspendMutation } from 'features/auth/queries/useAccountSuspendMutation'
import { resetFromRef } from 'features/navigation/navigationRef'
import { getProfileHookConfig } from 'features/navigation/navigators/ProfileStackNavigator/getProfileHookConfig'
import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { Adjust } from 'libs/adjust/adjust'
import { analytics } from 'libs/analytics/provider'
import { env } from 'libs/environment/env'
import { genericInfoPageIllustrationUrls } from 'shared/illustrations/genericInfoPageIllustrations'
import { BulletListItem } from 'ui/components/BulletListItem'
import { ExternalTouchableLink } from 'ui/components/touchableLink/ExternalTouchableLink'
import { VerticalUl } from 'ui/components/Ul'
import { Link } from 'ui/designSystem/Link/Link'
import { showErrorSnackBar } from 'ui/designSystem/Snackbar/snackBar.store'
import { GenericInfoPage } from 'ui/pages/GenericInfoPage'
import { ErrorIllustration } from 'ui/svg/icons/ErrorIllustration'
import { Typo } from 'ui/theme'
import { LINE_BREAK } from 'ui/theme/constants'

export function ConfirmDeleteProfile() {
  const signOut = useLogoutRoutine()

  const { suspendAccount, isLoading } = useAccountSuspendMutation({
    onSuccess: async () => {
      await signOut()
      // We use resetFromRef instead of navigation because signOut() may unmount the current screen
      // (RootNavigator rebuild). resetFromRef ensures navigation still works after logout.
      resetFromRef(...getProfileHookConfig('DeactivateProfileSuccess'))
    },
    onError: () => {
      showErrorSnackBar('Une erreur s’est produite pendant le chargement.')
    },
  })

  const groupLabel = 'Les conséquences'

  return (
    <GenericInfoPage
      withGoBack
      illustration={ErrorIllustration}
      remoteIllustration={{
        url: genericInfoPageIllustrationUrls.trashMosaic,
        backgroundColor: 'negative01',
      }}
      title="Veux-tu vraiment supprimer ton compte&nbsp;?"
      buttonPrimary={{
        wording: 'Supprimer mon compte',
        isLoading: isLoading,
        onPress: () => {
          Adjust.gdprForgetMe()
          suspendAccount()
        },
      }}
      buttonTertiary={{
        wording: 'Consulter l’article d’aide',
        externalNav: { url: env.FAQ_LINK_DELETE_ACCOUNT },
        onBeforeNavigate: analytics.logConsultArticleAccountDeletion,
      }}>
      <Typo.BodyAccent>{groupLabel}&nbsp;:</Typo.BodyAccent>
      <VerticalUl>
        {LINE_BREAK}
        <BulletListItem
          groupLabel={groupLabel}
          index={0}
          total={3}
          text="tes réservations sont annulées sauf pour certains cas précisés dans les "
          accessibilityRole={AccessibilityRole.LINK}>
          {LINE_BREAK}
          <ExternalTouchableLink
            as={Link}
            isInsideText
            color="neutral"
            wording="conditions générales d’utilisation"
            externalNav={{ url: env.CGU_LINK }}
            accessibilityRole={AccessibilityRole.LINK}
          />
        </BulletListItem>
        <BulletListItem
          groupLabel={groupLabel}
          index={1}
          total={3}
          text="si tu as un dossier en cours, tu ne pourras pas en déposer un nouveau"
        />
        <BulletListItem
          groupLabel={groupLabel}
          index={2}
          total={3}
          text="tu n’auras plus accès au catalogue"
        />
      </VerticalUl>
      <StyledBodyAccent>Les données que nous conservons&nbsp;:</StyledBodyAccent>
      <Typo.Body>
        Nous gardons toutes les informations personnelles que tu nous as transmises lors de la
        vérification de ton identité.
      </Typo.Body>
    </GenericInfoPage>
  )
}

const StyledBodyAccent = styled(Typo.BodyAccent)(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.xl,
}))
